import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import {
  BASE_API_URL,
  IBoondock,
  IBoondockPoint,
  IMapPoint,
  ISearchQuery,
  ISubPhoto,
  LatLon,
  ListingPoint,
  SEARCH_API_URL,
  SearchResult,
} from '@curbnturf/entities';
import { isNumber } from '@curbnturf/helpers';
import {
  BoondockDbSet,
  BoondockPhoto,
  BoondockPhotosDbSet,
  CachedHttpClient,
  DataCacheDbSet,
  Logger,
} from '@curbnturf/network';
import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

// @todo move this into the ListingSearch Service...if it's still useful.

@Injectable({
  providedIn: 'root',
})
export class BoondockService {
  constructor(
    private http: CachedHttpClient,
    private logger: Logger,
    private dataCache: DataCacheDbSet,
    private boondockDbSet: BoondockDbSet,
    private boondockPhotosDbSet: BoondockPhotosDbSet,
    @Inject(PLATFORM_ID) private platformId: string,
    private transferState: TransferState,
  ) {}

  find(query: ISearchQuery, limit?: number, page?: number): Observable<SearchResult[]> {
    let queryString = `${SEARCH_API_URL}?${query.toString()}`;
    if (limit) queryString += `&limit=${limit}`;
    if (page) queryString += `&page=${limit}`;

    return this.http
      .get(queryString, undefined, {
        offlineCallback: () => [],
      })
      .pipe(
        map((data) => {
          return data.map(
            (mapPoint: IMapPoint) =>
              new SearchResult({
                type: 'listing',
                name: mapPoint.name,
                coordinates: mapPoint.coordinates,
                poi: undefined,
                mapPoint,
              }),
          );
        }),
      );
  }

  fetch(boondockId: number | string): Observable<IBoondock> {
    const boondockStateKey = makeStateKey<IBoondock>('boondock-' + boondockId);

    if (this.transferState.hasKey(boondockStateKey)) {
      const boondock = this.transferState.get<IBoondock>(boondockStateKey, {});
      this.transferState.remove(boondockStateKey);
      return of(boondock);
    } else {
      return this.http
        .get<IBoondock>(`${BASE_API_URL}boondock/${boondockId}`, undefined, {
          offlineCallback: () => ({}),
        })
        .pipe(
          tap((listing) => {
            if (isPlatformServer(this.platformId)) {
              this.transferState.set(boondockStateKey, listing);
            }
          }),
        );
    }
  }

  public async preload(): Promise<void> {
    // todo: get a boondock query URL
    const queryString = `${SEARCH_API_URL}`;

    this.logger.debug('Preloading Boondock Data');

    try {
      this.http
        .get(queryString, undefined, {
          offlineCallback: () => [],
        })
        .subscribe({
          next: async (results: IBoondockPoint[]) => {
            const boondockRecords: IBoondockPoint[] = [];
            const boondockPhotoRecords: BoondockPhoto[] = [];

            results.forEach((mapPoint: IBoondockPoint) => {
              if (mapPoint) {
                boondockRecords.push(
                  new ListingPoint({
                    ...mapPoint,
                  }),
                );

                if (mapPoint.photos && Array.isArray(mapPoint.photos)) {
                  const photos: ISubPhoto[] = mapPoint.photos;
                  boondockPhotoRecords.push(
                    ...photos.map((el: ISubPhoto) => ({
                      boondockId: mapPoint?.id || 0,
                      key: el.key,
                      caption: el.caption || '',
                    })),
                  );
                }
              }
            });

            // Save the set
            await this.boondockDbSet.truncateAndStoreArray(boondockRecords);
            await this.boondockPhotosDbSet.truncateAndStoreArray(boondockPhotoRecords);

            await this.dataCache.store({
              cacheName: 'listings',
              lastModified: DateTime.now().toMillis(),
            });
          },
          error: (e) => {
            this.logger.error('Failed to Preload Listing Data', JSON.stringify(e));
          },
        });
    } catch (e) {
      this.logger.error('Failed to Preload Listing Data', JSON.stringify(e));
    }
  }

  async retrieveCachedBoondockListings(
    query: ISearchQuery,
    currentLocation: LatLon,
    take?: number,
    skip?: number,
  ): Promise<IMapPoint[]> {
    const results = await this.boondockDbSet.retrieveFiltered(query, take, skip);

    // calculate and sort by distance from current location.
    results.sort((a, b) => {
      const aDistance = a.distance
        ? a.distance
        : currentLocation.distanceTo(new LatLon({ lat: a.coordinates.lat, lon: a.coordinates.lon }));
      a.distance = aDistance;
      const bDistance = b.distance
        ? b.distance
        : currentLocation.distanceTo(new LatLon({ lat: b.coordinates.lat, lon: b.coordinates.lon }));
      b.distance = bDistance;
      return aDistance - bDistance;
    });

    // Get the photo records
    const photoResults = await this.boondockPhotosDbSet.retrieveBatch(
      results.map((el) => el.id).filter((id) => isNumber(id)) as number[],
    );
    results.forEach((boondock) => {
      boondock.photos = [...photoResults.filter((photo) => photo.boondockId === boondock.id)];
    });

    return results;
  }
}
